import React, { useEffect, useState, Component, ReactNode } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";
import { geolocationService } from "@/utils/geolocation-service";

interface AnalyticsProviderProps {
  children: React.ReactNode;
  pageTitle?: string;
  pageCategory?: string;
  conversionEvents?: string[];
}

// Error boundary to catch hook errors
class AnalyticsErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.debug("[AnalyticsErrorBoundary] Caught error:", error.message);
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.debug("[AnalyticsErrorBoundary] Error caught:", error);
  }

  render() {
    if (this.state.hasError) {
      console.debug(
        "[AnalyticsErrorBoundary] Rendering fallback without PageTracker",
      );
      // If analytics fails, just render children without tracking
      return this.props.children;
    }
    return this.props.children;
  }
}

// Separate tracking component to isolate hook calls
const PageTracker: React.FC<AnalyticsProviderProps> = ({
  pageTitle,
  pageCategory,
  conversionEvents = [],
}) => {
  // Don't render hooks on the server side
  if (typeof window === "undefined") {
    return null;
  }

  const { trackPageView, trackConversion, isInitialized, getSession } =
    useAnalytics();
  const [location, setLocation] = useState<{
    pathname: string;
    search: string;
  }>({
    pathname: window.location.pathname || "/",
    search: window.location.search || "",
  });

  useEffect(() => {
    const updateLocation = () => {
      setLocation({
        pathname: window.location.pathname,
        search: window.location.search,
      });
    };

    if (typeof window !== "undefined") {
      updateLocation();
      window.addEventListener("hashchange", updateLocation);
      window.addEventListener("popstate", updateLocation);

      return () => {
        window.removeEventListener("hashchange", updateLocation);
        window.removeEventListener("popstate", updateLocation);
      };
    }
  }, []);

  useEffect(() => {
    if (isInitialized && typeof window !== "undefined") {
      trackPageView(location.pathname + location.search, {
        title: pageTitle || document.title,
        category: pageCategory,
        url: window.location.href,
        referrer: document.referrer,
        timestamp: Date.now(),
      });

      conversionEvents.forEach((event) => {
        trackConversion("page_load", 0, {
          type: event,
          page: location.pathname,
        });
      });
    }
  }, [
    isInitialized,
    location.pathname,
    location.search,
    pageTitle,
    pageCategory,
    trackPageView,
    trackConversion,
  ]);

  useEffect(() => {
    const attachLocation = async () => {
      try {
        const sess = getSession?.();
        if (!sess?.sessionId) return;

        const markerKey = `analytics_loc_attached_${sess.sessionId}`;
        if (sessionStorage.getItem(markerKey)) return;

        const loc = await geolocationService.getUserLocation(false);
        if (!loc) {
          sessionStorage.setItem(markerKey, "1");
          return;
        }

        const host = window.location.hostname || "";
        const isValidHost =
          host.endsWith("tigola.com") ||
          host === "localhost" ||
          host.startsWith("127.") ||
          host.startsWith("192.168.");

        if (!isValidHost) {
          sessionStorage.setItem(markerKey, "1");
          return;
        }

        const locationData = {
          latitude: loc.coordinates?.latitude,
          longitude: loc.coordinates?.longitude,
          city: loc.city,
          region: loc.region,
          country: loc.country,
        };

        try {
          await fetch("/api/analytics/location", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sessionId: sess.sessionId,
              location: locationData,
              utmSource:
                new URLSearchParams(window.location.search).get("src") ||
                new URLSearchParams(window.location.search).get("utm_source") ||
                undefined,
            }),
          });
        } catch (error) {
          console.debug("[Analytics] Location fetch failed:", error);
        }

        sessionStorage.setItem(markerKey, "1");
      } catch (error) {
        console.debug("[Analytics] Location attachment error:", error);
      }
    };

    if (isInitialized && typeof window !== "undefined") {
      const timer = setTimeout(() => {
        attachLocation().catch((err) => {
          console.debug("[Analytics] Location attachment promise error:", err);
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isInitialized, getSession]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const { hostname, pathname } = window.location;
      let p = pathname.toLowerCase();
      if (p !== "/") p = p.replace(/\/+$/, "");
      if (p === "/") p = "";
      const originCanonical = hostname.endsWith("tigola.com")
        ? "https://tigola.com"
        : window.location.origin;
      const canonicalUrl = originCanonical + p;

      let link = document.querySelector('link[rel="canonical"]');
      const isPageManaged =
        link?.getAttribute("data-managed-by") === "page-seo";

      if (isPageManaged) return;

      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        link.setAttribute("data-managed-by", "analytics");
        document.head.appendChild(link);
      }

      if (link.getAttribute("data-managed-by") !== "page-seo") {
        link.setAttribute("href", canonicalUrl);
        link.setAttribute("data-managed-by", "analytics");
      }
    } catch (error) {
      console.debug("[Analytics] Canonical URL setup error:", error);
    }
  }, [location.pathname]);

  return null;
};

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  ...props
}) => {
  return <AnalyticsErrorBoundary>{children}</AnalyticsErrorBoundary>;
};

export default AnalyticsProvider;
